import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Public pages
import Assessment from './pages/Assessment';
import Results from './pages/Results';
import ProfessionalReport from './pages/ProfessionalReport';

// Admin pages
import Login from './pages/admin/Login';
import Dashboard from './pages/admin/Dashboard';
import Organizations from './pages/admin/Organizations';
import JobRoles from './pages/admin/JobRoles';
import AssessmentLinks from './pages/admin/AssessmentLinks';
import Assessments from './pages/admin/Assessments';

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tsvmap-orange" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// Public route wrapper (redirects to admin if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tsvmap-orange" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

// Home page - redirect based on auth
const Home = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tsvmap-orange" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  return <Navigate to="/admin/login" replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Home route */}
          <Route path="/" element={<Home />} />

          {/* Public assessment routes */}
          <Route path="/assess/:linkUuid" element={<Assessment />} />

          {/* Results routes - multiple report formats */}
          <Route path="/results/:uuid" element={<Results />} />
          <Route path="/v/:uuid" element={<Results view="full" />} />
          <Route path="/v/:uuid/concise" element={<Results view="concise" />} />
          <Route path="/v/:uuid/conciseone" element={<Results view="conciseone" />} />
          <Route path="/v/:uuid/concisetwo" element={<Results view="concisetwo" />} />

          {/* Professional Report - Full multi-page format */}
          <Route path="/report/:uuid" element={<ProfessionalReport />} />

          {/* Admin routes */}
          <Route
            path="/admin/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/organizations"
            element={
              <ProtectedRoute>
                <Organizations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/job-roles"
            element={
              <ProtectedRoute>
                <JobRoles />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/links"
            element={
              <ProtectedRoute>
                <AssessmentLinks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/assessments"
            element={
              <ProtectedRoute>
                <Assessments />
              </ProtectedRoute>
            }
          />

          {/* 404 - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
